/* eslint-disable complexity */
import format from 'date-fns/format';
import { select, call, put } from 'redux-saga/effects';
import {
  parseBoolean,
  getEddSessionState,
  getDateRange,
  getBasketId,
  getSessionStorage,
  getDateObject,
} from '@tcp/core/src/utils';
import {
  getEddABState,
  getItemsWithEDD,
  getOrderEddDates,
} from '@tcp/core/src/components/features/CnC/common/molecules/EddComponent/container/EddComponent.selectors';
import {
  setAfterPayInProgress,
  setVenmoPaymentInProgress,
} from '@tcp/core/src/components/features/CnC/Checkout/container/Checkout.action';
import { setPaymentMethod } from '@tcp/core/src/components/features/CnC/BagPage/container/BagPage.actions';
import {
  getDefaultAfterPayAbTest,
  getOverrideDefaultPaymentMethod,
  getIsAfterPayEnabled,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { getAfterPayPayload } from '@tcp/core/src/components/common/organisms/AddressVerification/container/AddressVerification.selectors';
import { getContactFormValuesFromSS } from '@tcp/core/src/components/features/CnC/SinglePageCheckout/molecules/ContactForm/ContactForm.util';
import { getCreditCardDefault } from '../../../account/Payment/container/Payment.selectors';
import { NAVIGATION_START } from '../../../../../constants/rum.constants';
import { isCanada } from '../../../../../utils';
import { convertToValidDate } from '../../../../../utils/parseDate';
import CheckoutSelector from '../../Checkout/container/Checkout.selector';
import { startEddPersist } from '../../../../../services/abstractors/CnC/CheckoutSFCC';
import constants from '../../Checkout/Checkout.constants';
import { SHIPMENT_ID } from './SinglePageCheckout.constants';
import {
  checkDefaultAfterPayEnabled,
  getIsExtendedPreOrderForSPC,
} from './SinglePageCheckout.selector';
import { recalibrateSectionStates } from './SinglePageCheckout.actions';

export const extractMonthAndYear = (expMonthAndYear) => {
  const validDate = convertToValidDate(expMonthAndYear);
  const formattedDate = (validDate && format(getDateObject(validDate), 'MM/yyyy')) || '';
  return formattedDate.split('/');
};

export const deStructureAddress = (address) => {
  const {
    onFileAddressId: addressId,
    sameAsShipping,
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    state,
    city,
    zipCode,
    country,
  } = address;

  return {
    addressId,
    sameAsShipping,
    firstName,
    lastName,
    addressLine1,
    addressLine2,
    state,
    city,
    country,
    zipCode,
  };
};

const getCountry = (country) => {
  return isCanada() ? 'CA' : country;
};

export const getShippingPayloadForSubmit = ({
  savedAddress,
  savedContactInfo,
  giftServicesDetails,
  isGuestUser,
  modifiedShippingMethod,
  smsSignUpFromSection,
  emailSignUp,
  loggedInUserEmail,
  loggedInUserPhone,
  orderPhoneNo,
  contactDetail,
  isLoginV2FlowEnabled,
  orderEmail,
  contactSectionData,
  checkoutShippingAddress,
}) => {
  const getSSContactInfo = getContactFormValuesFromSS(contactSectionData);
  const payload = {
    address: {
      firstName: savedAddress?.firstName,
      lastName: savedAddress?.lastName,
      addressLine1: savedAddress?.addressLine1,
      addressLine2: savedAddress?.addressLine2,
      zipCode: savedAddress?.zipCode,
      city: savedAddress?.city,
      state: savedAddress?.state,
      country: getCountry(savedAddress?.country),
      addressId: savedAddress?.addressId,
      isCommercialAddress: savedAddress?.isCommercialAddress,
      validationCode: savedAddress?.validationCode,
      phoneCountryCode: savedAddress?.phoneCountryCode || checkoutShippingAddress?.phoneCountryCode,
      phone:
        (isLoginV2FlowEnabled ? savedAddress?.phoneNumber : savedContactInfo?.phoneNumber) ||
        loggedInUserPhone ||
        orderPhoneNo,
    },
    emailAddress:
      (isGuestUser
        ? savedContactInfo?.emailAddress || getSSContactInfo?.emailAddress
        : loggedInUserEmail) ||
      orderEmail ||
      (isLoginV2FlowEnabled && savedAddress?.emailAddress),
    emailSignUp: {
      emailSignUpTCP: emailSignUp?.emailSignUp?.emailSignUpTCP,
      emailSignUpGYM: emailSignUp?.emailSignUp?.emailSignUpGYM,
    },
    hasSetGiftOptions: !!giftServicesDetails?.optionId,
    isEmailSignUpAllowed: true,
    isGuestUser,
    method: { shippingMethodId: modifiedShippingMethod },
    phoneCountryCode: savedAddress?.phoneCountryCode,
    phoneNumber:
      (isLoginV2FlowEnabled && savedAddress?.phoneNumber) ||
      savedContactInfo?.phoneNumber ||
      orderPhoneNo ||
      savedContactInfo?.phone ||
      contactDetail?.phoneNumber,
    saveToAccount: savedAddress?.saveToAddressBook,
    setAsDefault: savedAddress?.defaultShipping,
    smsInfo: {
      smsUpdateNumber: isLoginV2FlowEnabled
        ? savedAddress?.phoneNumber
        : savedContactInfo?.phoneNumber,
      wantsSmsOrderUpdates: !!smsSignUpFromSection?.sendOrderUpdate,
    },
  };

  if (giftServicesDetails && giftServicesDetails?.optionId) {
    const giftServicesData = {
      brand: giftServicesDetails.brand,
      catEntryId: giftServicesDetails.optionId,
      GiftMsg: giftServicesDetails.message,
      hasGiftWrapping: giftServicesDetails.hasGiftWrapping,
    };

    payload.giftServicesData = giftServicesData;
  }
  return payload;
};

export function* getSavedShippingValues() {
  const { address, method, deliveryIndicator } = yield select(
    CheckoutSelector.getShippingDestinationValues
  );
  const giftServicesDetails = yield select(CheckoutSelector.getGiftWrappingValues) || {};

  const { addressLine1: address1, addressLine2: address2, zipCode: zip, ...others } = address;

  return {
    ...others,
    address1,
    address2,
    zip,
    shipmentMethods: method,
    isCommercialAddress: deliveryIndicator === 'B',
    giftServicesDetails,
  };
}

export function addPerformanceMarksForSection(sectionMark) {
  performance.clearMarks(`${sectionMark}:start`);
  performance.mark(`${sectionMark}:start`);
  performance.clearMarks(NAVIGATION_START);
  performance.mark(NAVIGATION_START);
}

const restructureEddData = (itemsWithEdd = [], selectedShippingCode, isGetOfferPayload = false) => {
  return itemsWithEdd.map((itm) => ({
    c_edd: itm?.shippingDates[selectedShippingCode],
    itemId: isGetOfferPayload ? itm?.itemPartNumber : itm.orderItemId,
    productId: itm?.itemPartNumber,
    quantity: itm.quantity,
    c_node: itm.node,
    c_nodeProcessingTime: itm?.nodeProcessingTime[selectedShippingCode],
    c_transitTime: itm?.transitTime[selectedShippingCode],
    c_carrier: itm.carrier && itm.carrier[selectedShippingCode],
  }));
};

const getShipmentMethod = (itemsWithEdd) => {
  const shippingDates = itemsWithEdd && itemsWithEdd[0] && itemsWithEdd[0].shippingDates;
  let selectedShipmentMethod = '';
  if (shippingDates) {
    if (shippingDates.UGNR) {
      selectedShipmentMethod = constants.SHIPPING_CODE.UGNR;
    } else if (shippingDates.PMDC) {
      selectedShipmentMethod = constants.SHIPPING_CODE.PMDC;
    } else if (shippingDates.U2AK) {
      selectedShipmentMethod = constants.SHIPPING_CODE.U2AK;
    }
  }
  return selectedShipmentMethod;
};

export const formatEddPersistParams = ({
  itemsWithEdd,
  orderEddDates,
  selectedShippingCode,
  isGetOfferPayload = false,
}) => {
  const shippingCode = !selectedShippingCode
    ? getShipmentMethod(itemsWithEdd)
    : selectedShippingCode;
  return {
    shipmentId: SHIPMENT_ID,
    orderItemsList: itemsWithEdd
      ? restructureEddData(itemsWithEdd, shippingCode, isGetOfferPayload)
      : null,
    c_orderEdd:
      (shippingCode && orderEddDates && getDateRange(orderEddDates, shippingCode, true)) || '',
  };
};

/* ******

Function implemented to call Persist Api

******** */

export function* callPersistData() {
  const basketId = getBasketId();
  const shippingMethods = yield select(CheckoutSelector.getShipmentMethods);
  const shipmentId = yield select(CheckoutSelector.getSavedShipmentId);
  const itemsWithEdd = yield select(getItemsWithEDD);
  const selectedShipmentMethod =
    shippingMethods && shippingMethods.find((shipData) => shipData.id === shipmentId);
  const orderEddDates = yield select(getOrderEddDates);
  const payload = formatEddPersistParams({
    itemsWithEdd,
    orderEddDates,
    selectedShippingCode: selectedShipmentMethod?.code,
  });

  if (payload && payload.orderItemsList && payload.orderItemsList.length) {
    const eddABState = yield select(getEddABState);
    const isEddABStateFlag = parseBoolean(getEddSessionState(eddABState));
    if (isEddABStateFlag) {
      payload.c_showEDD = 'true';
    }
    yield call(startEddPersist, payload, basketId);
  }
}

export const getContactFormInitValues = ({
  isContactFormSavedInSS,
  shippingContactInfo,
  contactFormValuesFromSS,
}) => {
  const {
    emailAddress: savedEmailAddress,
    phoneNumber: savedPhoneNumber,
    smsSignUp: savedSmsSignUp,
  } = shippingContactInfo;
  // ContactSectionState: Initialize from saved shipping
  let contactSectionState = {
    savedContactInfo: {
      emailAddress: savedEmailAddress,
      phoneNumber: savedPhoneNumber,
    },
    smsSignUp: savedSmsSignUp,
  };
  if (isContactFormSavedInSS) {
    // ContactSectionState: Initialize from session storage
    const {
      emailAddress: emailAddressFromSS,
      phoneNumber: phoneNumberFromSS,
      smsSignUp: smsSignUpFromSS,
    } = contactFormValuesFromSS;
    contactSectionState = {
      savedContactInfo: {
        emailAddress: emailAddressFromSS,
        phoneNumber: phoneNumberFromSS,
      },
      smsSignUp: smsSignUpFromSS,
    };
  }
  return contactSectionState;
};

export function* setAfterPayPaymentmethod() {
  const afterPayPayload = yield select(getAfterPayPayload);
  const isDefaultAfterPayAbTest = yield select(getDefaultAfterPayAbTest);
  const defaultPayment = yield select(getCreditCardDefault);
  const defaultPaymentFormat = JSON.parse(JSON.stringify(defaultPayment));
  const overideDefaultPaymentMethod = yield select(getOverrideDefaultPaymentMethod);
  const afterPaySS = parseBoolean(getSessionStorage('defaultAfterPay'));
  let changeDefaultPaymentmethod = true;
  if (defaultPaymentFormat && defaultPaymentFormat?.length > 0) {
    if (overideDefaultPaymentMethod) {
      changeDefaultPaymentmethod = true;
    } else {
      changeDefaultPaymentmethod = false;
    }
  }
  const isExtendedPreOrderForSpc = yield select(getIsExtendedPreOrderForSPC);
  const isAfterPayEnabled = yield select(getIsAfterPayEnabled);
  const isDefaultAfterPay = yield select(checkDefaultAfterPayEnabled);
  const isAfterPayInProgress = yield select(CheckoutSelector.getIsAfterPayInProgress);
  if (
    (isDefaultAfterPay &&
      isDefaultAfterPayAbTest &&
      changeDefaultPaymentmethod &&
      !isExtendedPreOrderForSpc &&
      isAfterPayEnabled &&
      afterPaySS) ||
    afterPayPayload?.isMallisaFailed ||
    isAfterPayInProgress
  ) {
    yield put(setAfterPayInProgress(true));
    yield put(setVenmoPaymentInProgress(false));
    yield put(setPaymentMethod('AFTERPAY'));
    yield put(recalibrateSectionStates());
  } else {
    yield put(setAfterPayInProgress(false));
    yield put(setPaymentMethod(''));
  }
}
